import { styled } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import { ProposalFieldCategoryType, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

const TabLabelContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "2px",
}));

const BadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "2px",
}));

const StyledBadge = styled(Badge)(() => ({
  width: "16px",
  height: "16px",
  "& .MuiBadge-badge": {
    fontSize: "0.6rem",
    height: "16px",
    minWidth: "16px",
  },
}));

const WarningIssueBadge = styled(StyledBadge)(() => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#FFA500",
    color: "#fff"
  },
}));

const PendingCommentBadge = styled(StyledBadge)(() => ({}));

const CommentBadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 0,
  "& > .MuiBadge-root + .MuiBadge-root": {
    marginLeft: "-4px",
  },
}));

type ProposalIssuesBadgeProps = {
  issues?: ProposalIssues;
  commentThreads?: CommentThread[];
  category: ProposalFieldCategoryType;
  title?: string;
  pendingCommentCount?: number;
};

export default function ProposalIssuesBadge(props: Readonly<ProposalIssuesBadgeProps>) {
  const { issues, commentThreads, category, title, pendingCommentCount = 0 } = props;

  const filteredThreads = commentThreads?.filter(
    (comment) => comment.field.category === category && comment.field.name !== ProposalFieldName.General,
  );
  const filteredIssues = issues?.entries.filter((issue) => issue.field.category === category);
  const numErrors = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Critical).length ?? 0;
  const numWarnings = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Warning).length ?? 0;
  const numComments = filteredThreads?.reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  function getToolTip() {
    if (!filteredIssues && !filteredThreads) return "";

    const messages: React.ReactNode[] = [];

    if (filteredIssues) {
      for (const issue of filteredIssues) {
        messages.push(<p key={issue.field.name + (issue.field.id?.value ?? "")}>{issue.description}</p>);
      }
    }

    if (filteredThreads) {
      for (const [index, thread] of filteredThreads.entries()) {
        const unreadCount = thread.numUnreadMessages ?? 0;
        if (unreadCount === 0) continue;

        messages.push(
          <p
            key={
              thread.id?.value ??
              `${thread.proposalId.value}-${thread.field.cacheKey}-${thread.isExternal}-${thread.numUnreadMessages ?? 0}-${index}`
            }
          >
            {unreadCount} new comment{unreadCount > 1 ? "s" : ""}.
          </p>,
        );
      }
    }

    if (pendingCommentCount > 0) {
      messages.push(
        <p key={`pending-${category}`}>
          {pendingCommentCount} pending comment{pendingCommentCount > 1 && "s"} not yet shared.
        </p>,
      );
    }

    return messages;
  }

  return (
    <TabLabelContainer>
      <span>{title ?? category.toString()}</span>
      <Tooltip title={getToolTip()} placement="bottom">
        <BadgeContainer>
          {numErrors > 0 && <StyledBadge color="error" badgeContent={numErrors} />}
          {numWarnings > 0 && <WarningIssueBadge badgeContent={numWarnings} data-testid="issue-warning-badge" />}
          {(pendingCommentCount > 0 || numComments > 0) && (
            <CommentBadgeContainer>
              {pendingCommentCount > 0 && (
                <PendingCommentBadge color="warning" badgeContent={pendingCommentCount} data-testid="pending-comment-badge">
                  <Box sx={{ width: 16, height: 16 }} />
                </PendingCommentBadge>
              )}
              {numComments > 0 && (
                <StyledBadge color="secondary" badgeContent={numComments}>
                  <Box sx={{ width: 16, height: 16 }} />
                </StyledBadge>
              )}
            </CommentBadgeContainer>
          )}
        </BadgeContainer>
      </Tooltip>
    </TabLabelContainer>
  );
}
